import { useState, useCallback } from "react";
import { Apis } from "../../lib/Apis";

export const useServices = () => {
  const [loading, setLoading] = useState(false);

  const getServices = useCallback(async (
    setServiceData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = ""
  ) => {
    try {
      const params = {};
      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }
      const response = await Apis.getServices(offset, limit, params);
      if (response?.status === 200) {
        setServiceData(response.data.data ?? []);
        const totalRecords =
          response.data.pagination?.totalRecords ??
          response.data.totalCount ??
          response.data.data?.length ??
          0;
        setTotal(totalRecords);
        return response.data;
      } else {
        throw new Error(response?.data?.message || "Failed to fetch services.");
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      throw error;
    }
  }, []);

  const searchServices = useCallback(async (searchFilter, offset = 0, limit = 10) => {
    try {
      const params = {};
      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }
      const response = await Apis.getServices(offset, limit, params);
      return response.data;
    } catch (error) {
      console.error("Error searching services:", error);
      throw error;
    }
  }, []);

  const addService = useCallback(async (data) => {
    try {
      const response = await Apis.addService(data);
      return response.data;
    } catch (error) {
      console.error("Error adding service:", error);
      throw error;
    }
  }, []);

  const updateService = useCallback(async (id, data) => {
    try {
      const response = await Apis.updateService(id, data);
      return response.data;
    } catch (error) {
      console.error("Error updating service:", error);
      throw error;
    }
  }, []);

  const deleteService = useCallback(async (id) => {
    try {
      const response = await Apis.deleteService(id);
      return response.data;
    } catch (error) {
      console.error("Error deleting service:", error);
      throw error;
    }
  }, []);

  const getServiceById = useCallback(async (id, setServiceData) => {
    try {
      const response = await Apis.getServiceById(id);
      const service = response.data?.data ?? response.data;
      setServiceData(service);
      return service;
    } catch (error) {
      console.error("Error fetching service by id:", error);
      throw error;
    }
  }, []);

  const addServiceCategory = useCallback(async (data) => {
    try {
      setLoading(true);
      const response = await Apis.addServiceCategory(data);
      return response?.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateServiceCategory = useCallback(async (id, data) => {
    try {
      setLoading(true);
      const response = await Apis.updateServiceCategory(id, data);
      return response?.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteServiceCategory = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await Apis.deleteServiceCategory(id);
      return response?.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const getServicesCategories = useCallback(async (
    setCategories,
    setTotal,
    offset = 0,
    limit = 10,
    name = "",
  ) => {
    try {
      setLoading(true);
      const params = {};
      if (name && name.trim()) params.name = name.trim();

      const response = await Apis.getServicesCategories(offset, limit, params);
      if (response?.status === 200) {
        setCategories(response.data.data ?? []);
        setTotal(response.data.totalCount ?? 0);
        return response.data;
      }
      throw new Error(response?.data?.message || "Failed to fetch services categories.");
    } finally {
      setLoading(false);
    }
  }, []);

  const searchServicesCategories = useCallback(async (name, offset = 0, limit = 10) => {
    const params = {};
    if (name && name.trim()) params.name = name.trim();

    const response = await Apis.getServicesCategories(offset, limit, params);
    return response.data;
  }, []);

  const getServiceCategoriesForSelect = useCallback(async (setCategories) => {
    try {
      const response = await Apis.getServicesCategoriesForSelect();
      const categories = response?.data?.data ?? [];
      if (setCategories && typeof setCategories === "function") {
        setCategories(categories);
      }
      return categories;
    } catch (error) {
      console.error("Error fetching service categories:", error);
      throw error;
    }
  }, []);

  return {
    loading,
    getServices,
    searchServices,
    addService,
    updateService,
    deleteService,
    getServiceById,
    addServiceCategory,
    updateServiceCategory,
    deleteServiceCategory,
    getServicesCategories,
    searchServicesCategories,
    getServiceCategoriesForSelect,
  };
};
